/**
 * Magma Dice Component
 *
 * Renders the magma-themed dice using the actual Figma SVG design.
 * Features:
 * - Purple geometric faceted frame (#886F97, #7E6489, #695875)
 * - Player-specific orange-red body surface (horizontal linear gradient)
 * - Orange/amber corner torch elements (#F98403 → #FFB408)
 * - Gold sparkle decorations (#F6C634, #F5E48C)
 * - Dark purple shadow base (#2A1C35)
 * - Radial gradient inner face (black center → player accent edge)
 * - Flame-shaped pip indicators (#FF3100, #FF9716, #FFF526)
 *
 * SVG paths extracted from Figma red_dice.svg (viewBox 0 0 187 198).
 * Uses useDiceViewModel for shared animation/interaction logic.
 */

import React from 'react';
import { motion } from 'framer-motion';
import type { DiceProps } from '../basics/constants/types';
import type { MagmaDiceColorScheme } from './constants/diceColorSchemes';
import { useDiceViewModel } from '../basics/hooks/useDiceViewModel';
import { DICE_CONFIG } from '../basics/utils/utils';
import {
  MAGMA_SHADOW_PATHS,
  MAGMA_LEDGE_PATHS,
  MAGMA_BODY_PATH,
  MAGMA_CORNER_PATHS,
  MAGMA_FRAME_PATHS,
  MAGMA_EDGE_PATHS,
  MAGMA_INNER_FACE_PATHS,
  MAGMA_SPARKLES_PRIMARY,
  MAGMA_SPARKLES_SECONDARY,
  MAGMA_FLAME_FULL,
  MAGMA_PIP_POSITIONS
} from './constants/magmaDiceData';
import '../basics/Dice.css';

interface MagmaDiceProps extends DiceProps {
  colorScheme: MagmaDiceColorScheme;
}

// Fixed purple frame colors (from Figma)
const FRAME_PURPLE_LIGHT = '#886F97';
const FRAME_PURPLE_MID = '#7E6489';
const FRAME_PURPLE_DARK = '#695875';

// Fixed shadow color
const SHADOW_DARK = '#2A1C35';

// Corner torch orange gradient colors (fixed across all players)
const TORCH_ORANGE_START = '#F98403';
const TORCH_ORANGE_MID = '#FFB408';

// Flame pip colors (fixed)
const FLAME_OUTER = '#FF3100';
const FLAME_MID = '#FF9716';
const FLAME_INNER = '#FFF526';

const MagmaDiceComponent = React.memo(function MagmaDiceComponent({
  colorScheme,
  ...diceProps
}: MagmaDiceProps) {
  const {
    showDiceFace = true,
    showGround = true,
    isRolling = false,
    viewBox = '0 0 187 198'
  } = diceProps;

  const { state, actions } = useDiceViewModel(diceProps);

  const getId = state.getId;

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={viewBox}
      className={state.svgClassName}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={`Dice showing ${String(state.validValue)}`}
      aria-live={isRolling ? 'polite' : 'off'}
      focusable="false"
      onClick={actions.handleClick}
      style={{ cursor: state.cursorStyle }}
    >
      <defs>
        {/* Clip path */}
        <clipPath id={getId('clip')}>
          <rect width="186.67" height="197.33" fill="white" />
        </clipPath>

        {/* Drop shadow filter (for animated face group) */}
        <filter id={getId('dropShadow')} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceAlpha" stdDeviation={DICE_CONFIG.SHADOW_3D.BLUR_RADIUS} />
          <feOffset
            dx={DICE_CONFIG.SHADOW_3D.OFFSET_X}
            dy={DICE_CONFIG.SHADOW_3D.OFFSET_Y}
            result="offsetblur"
          />
          <feComponentTransfer>
            <feFuncA type="linear" slope={DICE_CONFIG.SHADOW_3D.OPACITY} />
          </feComponentTransfer>
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Body gradient — player-specific horizontal (paint2_linear) */}
        <linearGradient
          id={getId('body')}
          x1="15.81"
          y1="85.01"
          x2="170.86"
          y2="85.01"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colorScheme.bodyGradient[0]} />
          <stop offset="1" stopColor={colorScheme.bodyGradient[1]} />
        </linearGradient>

        {/* Ledge bottom gradient — dark purple fade (paint0_linear) */}
        <linearGradient
          id={getId('ledgeBottom')}
          x1="93.32"
          y1="165.8"
          x2="93.32"
          y2="194.24"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={SHADOW_DARK} />
          <stop offset="1" />
        </linearGradient>

        {/* Ledge top gradient — purple to black (paint1_linear) */}
        <linearGradient
          id={getId('ledgeTop')}
          x1="93.33"
          y1="155.66"
          x2="93.33"
          y2="189.41"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={FRAME_PURPLE_LIGHT} />
          <stop offset="1" />
        </linearGradient>

        {/* Corner torch gradients (paint3–8): 3-stop orange */}
        <linearGradient
          id={getId('torchA')}
          x1="20.23"
          y1="25.79"
          x2="32.65"
          y2="14.11"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={TORCH_ORANGE_START} />
          <stop offset="0.52" stopColor={TORCH_ORANGE_MID} />
          <stop offset="1" stopColor={TORCH_ORANGE_START} />
        </linearGradient>

        <linearGradient
          id={getId('torchB')}
          x1="19.29"
          y1="69.77"
          x2="48.16"
          y2="42.6"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={TORCH_ORANGE_START} />
          <stop offset="0.52" stopColor={TORCH_ORANGE_MID} />
          <stop offset="1" stopColor={TORCH_ORANGE_START} />
        </linearGradient>

        <linearGradient
          id={getId('torchC')}
          x1="142.12"
          y1="128.76"
          x2="158.57"
          y2="113.28"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={TORCH_ORANGE_START} />
          <stop offset="0.52" stopColor={TORCH_ORANGE_MID} />
          <stop offset="1" stopColor={TORCH_ORANGE_START} />
        </linearGradient>

        <linearGradient
          id={getId('torchD')}
          x1="147.69"
          y1="153.43"
          x2="163.96"
          y2="138.12"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={TORCH_ORANGE_START} />
          <stop offset="0.52" stopColor={TORCH_ORANGE_MID} />
          <stop offset="1" stopColor={TORCH_ORANGE_START} />
        </linearGradient>

        <linearGradient
          id={getId('torchE')}
          x1="35.87"
          y1="154.55"
          x2="119.58"
          y2="154.55"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={TORCH_ORANGE_START} />
          <stop offset="0.52" stopColor={TORCH_ORANGE_MID} />
          <stop offset="1" stopColor={TORCH_ORANGE_START} />
        </linearGradient>

        <linearGradient
          id={getId('torchF')}
          x1="87.74"
          y1="14.64"
          x2="162.97"
          y2="14.64"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={TORCH_ORANGE_START} />
          <stop offset="0.52" stopColor={TORCH_ORANGE_MID} />
          <stop offset="1" stopColor={TORCH_ORANGE_START} />
        </linearGradient>

        {/* Edge fade gradients (dark purple → black, for transition paths) */}
        <linearGradient
          id={getId('edgeTopA')}
          x1="102.18"
          y1="26.19"
          x2="102.18"
          y2="31.96"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={SHADOW_DARK} />
          <stop offset="1" />
        </linearGradient>

        <linearGradient
          id={getId('edgeRightA')}
          x1="156.04"
          y1="85.93"
          x2="156.04"
          y2="100.19"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={SHADOW_DARK} />
          <stop offset="1" />
        </linearGradient>

        <linearGradient
          id={getId('edgeRightB')}
          x1="32.82"
          y1="108.47"
          x2="32.82"
          y2="113.91"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={SHADOW_DARK} />
          <stop offset="1" />
        </linearGradient>

        <linearGradient
          id={getId('edgeRightC')}
          x1="151.04"
          y1="86.27"
          x2="151.04"
          y2="95.53"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={SHADOW_DARK} />
          <stop offset="1" />
        </linearGradient>

        {/* Inner face — radial gradient, dark center to player-color edge (paint13_radial) */}
        <radialGradient
          id={getId('faceRadial')}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(94.978 53.2) rotate(90) scale(127.18 249.273)"
        >
          <stop />
          <stop offset="1" stopColor={colorScheme.faceRadialColor} />
        </radialGradient>

        {/* Inner face horizontal vignette (paint14_linear) */}
        <linearGradient
          id={getId('faceVigtH')}
          x1="29.29"
          y1="85.06"
          x2="158.61"
          y2="85.06"
          gradientUnits="userSpaceOnUse"
        >
          <stop />
          <stop offset="0.08" stopOpacity="0" />
          <stop offset="0.92" stopOpacity="0" />
          <stop offset="1" />
        </linearGradient>

        {/* Inner face vertical vignette (paint15_linear) */}
        <linearGradient
          id={getId('faceVigtV')}
          x1="93.83"
          y1="20.57"
          x2="93.83"
          y2="149.89"
          gradientUnits="userSpaceOnUse"
        >
          <stop />
          <stop offset="0.08" stopOpacity="0" />
          <stop offset="0.92" stopOpacity="0" />
          <stop offset="1" />
        </linearGradient>
      </defs>

      <g clipPath={`url(#${getId('clip')})`}>
        {/* Fixed ground/background (purple frame + inner face) — only when showGround */}
        {showGround && (
          <>
            {/* ── Drop shadow base (opacity 0.12) ── */}
            <g opacity="0.12">
              <path d={MAGMA_SHADOW_PATHS.SHADOW_A} fill="black" />
              <path d={MAGMA_SHADOW_PATHS.SHADOW_B} fill="black" />
              <path d={MAGMA_SHADOW_PATHS.SHADOW_OUTER_ROUND} fill="black" />
              <path d={MAGMA_SHADOW_PATHS.SHADOW_INNER_ROUND_A} fill="black" />
              <path d={MAGMA_SHADOW_PATHS.SHADOW_INNER_ROUND_B} fill="black" />
            </g>

            {/* ── Ledge / Ground layers ── */}
            <path d={MAGMA_LEDGE_PATHS.LEDGE_BOTTOM} fill={`url(#${getId('ledgeBottom')})`} />
            <path d={MAGMA_LEDGE_PATHS.LEDGE_TOP} fill={`url(#${getId('ledgeTop')})`} />

            {/* ── Main body — player color ── */}
            <path d={MAGMA_BODY_PATH} fill={`url(#${getId('body')})`} />

            {/* ── Corner torch decorations — orange gradient ── */}
            <path d={MAGMA_CORNER_PATHS.TOP_LEFT_A} fill={`url(#${getId('torchA')})`} />
            <path d={MAGMA_CORNER_PATHS.TOP_LEFT_B} fill={`url(#${getId('torchB')})`} />
            <path d={MAGMA_CORNER_PATHS.RIGHT_A} fill={`url(#${getId('torchC')})`} />
            <path d={MAGMA_CORNER_PATHS.RIGHT_B} fill={`url(#${getId('torchD')})`} />
            <path d={MAGMA_CORNER_PATHS.BOTTOM_LEFT} fill={`url(#${getId('torchE')})`} />
            <path d={MAGMA_CORNER_PATHS.TOP_RIGHT} fill={`url(#${getId('torchF')})`} />

            {/* ── Gold sparkle decorations — primary (#F6C634) ── */}
            {MAGMA_SPARKLES_PRIMARY.map((path, index) => (
              <path key={`sp-${String(index)}`} d={path} fill="#F6C634" />
            ))}

            {/* ── Gold sparkle decorations — secondary highlights (#F5E48C) ── */}
            {MAGMA_SPARKLES_SECONDARY.map((path, index) => (
              <path key={`ss-${String(index)}`} d={path} fill="#F5E48C" />
            ))}

            {/* ── Dark purple shadow area ── */}
            <path d={MAGMA_FRAME_PATHS.SHADOW_AREA} fill={SHADOW_DARK} />

            {/* ── Purple geometric frame facets — light (#886F97) ── */}
            {MAGMA_FRAME_PATHS.FACET_886F97.map((path, index) => (
              <path key={`fl-${String(index)}`} d={path} fill={FRAME_PURPLE_LIGHT} />
            ))}

            {/* ── Purple geometric frame facets — mid (#7E6489) ── */}
            {MAGMA_FRAME_PATHS.FACET_7E6489.map((path, index) => (
              <path key={`fm-${String(index)}`} d={path} fill={FRAME_PURPLE_MID} />
            ))}

            {/* ── Purple geometric frame facets — dark (#695875) ── */}
            {MAGMA_FRAME_PATHS.FACET_695875.map((path, index) => (
              <path key={`fd-${String(index)}`} d={path} fill={FRAME_PURPLE_DARK} />
            ))}

            {/* ── Edge transition fade paths ── */}
            <path d={MAGMA_EDGE_PATHS.TOP_EDGE_A} fill={`url(#${getId('edgeTopA')})`} />
            <path d={MAGMA_EDGE_PATHS.RIGHT_EDGE_A} fill={`url(#${getId('edgeRightA')})`} />
            <path d={MAGMA_EDGE_PATHS.RIGHT_EDGE_B} fill={`url(#${getId('edgeRightB')})`} />
            <path d={MAGMA_EDGE_PATHS.RIGHT_EDGE_C} fill={`url(#${getId('edgeRightC')})`} />
          </>
        )}

        {/* Dice face plate — the inner rounded-square that holds the flame pips.
            Part of the tumbling dice body (with the flames below), NOT the ground. */}
        {showDiceFace && (
          <>
            {/* ── Inner face — radial gradient (black center → player accent) ── */}
            <path d={MAGMA_INNER_FACE_PATHS.FACE_RADIAL} fill={`url(#${getId('faceRadial')})`} />
            {/* Horizontal vignette (darkens left/right edges) */}
            <path
              d={MAGMA_INNER_FACE_PATHS.FACE_VIGNETTE_HORIZ}
              fill={`url(#${getId('faceVigtH')})`}
            />
            {/* Vertical vignette (darkens top/bottom edges) */}
            <path
              d={MAGMA_INNER_FACE_PATHS.FACE_VIGNETTE_VERT}
              fill={`url(#${getId('faceVigtV')})`}
            />
          </>
        )}

        {/* ── Animated dice pips (flame indicators) ── */}
        {showDiceFace && (
          <motion.g
            animate={state.combinedAnimation}
            className="dice-3d-transform"
            style={{
              transformOrigin: '93.5px 85px',
              opacity: isRolling ? DICE_CONFIG.INACTIVE_OPACITY : 1
            }}
            filter={`url(#${getId('dropShadow')})`}
          >
            {state.displayedValue === 1 ? (
              // Value 1: full-size flame centered on the face
              <g>
                <path d={MAGMA_FLAME_FULL.outerFlame} fill={FLAME_OUTER} />
                <path d={MAGMA_FLAME_FULL.midFlame} fill={FLAME_MID} />
                <path d={MAGMA_FLAME_FULL.innerFlame} fill={FLAME_INNER} />
              </g>
            ) : (
              // Values 2–6: scaled flame copies at standard pip positions
              (MAGMA_PIP_POSITIONS[state.displayedValue] ?? []).map((pos, index) => (
                <g
                  key={index}
                  transform={`translate(${String(pos.x)}, ${String(pos.y)}) scale(${String(pos.scale)})`}
                >
                  <path d={MAGMA_FLAME_FULL.outerFlame} fill={FLAME_OUTER} />
                  <path d={MAGMA_FLAME_FULL.midFlame} fill={FLAME_MID} />
                  <path d={MAGMA_FLAME_FULL.innerFlame} fill={FLAME_INNER} />
                </g>
              ))
            )}
          </motion.g>
        )}
      </g>
    </svg>
  );
});

export default MagmaDiceComponent;
